// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
/**
 * Mt Forge - License Manager Page
 *
 * Full admin dashboard for managing license keys across all Forge apps.
 * Lives in MT Forge (Internal) at /licenses
 *
 * Features:
 * - Stats overview (total keys, activations, revenue estimate, expiring soon)
 * - Generate new keys (single or batch)
 * - Keys table with filtering, search, and actions
 * - Per-key actions: copy, revoke, renew, view activations
 * - Demo data baked in for immediate use
 */

import React, { useState, useMemo } from 'react';

// ============================================================
// APP REGISTRY - All Forge apps
// ============================================================
const APP_LIST = [
  { slug: 'all', label: 'Universal (All Apps)' },
  { slug: 'croppatrol', label: 'CropPatrol' },
  { slug: 'forge-fleet', label: 'Forge Fleet' },
  { slug: 'directional-ops', label: 'Directional Ops' },
  { slug: 'lotsync', label: 'LotSync' },
  { slug: 'nursevault', label: 'NurseVault' },
  { slug: 'nursevault-ca', label: 'NurseVault CA' },
  { slug: 'vantage-ops', label: 'Vantage Ops' },
  { slug: 'budledger', label: 'BudLedger' },
  { slug: 'ironledger', label: 'OreSync' },
  { slug: 'haulsync', label: 'VacTraq' },
  { slug: 'glowdesk', label: 'GlowDesk' },
  { slug: 'closingdesk', label: 'ClosingDesk' },
  { slug: 'wrenchsync', label: 'WrenchSync' },
  { slug: 'treadops', label: 'TreadOps' },
  { slug: 'herdledger', label: 'HerdLedger' },
  { slug: 'vitasync', label: 'VitaSync' },
  { slug: 'policyforge', label: 'PolicyForge' },
  { slug: 'stagevault', label: 'StageVault' },
  { slug: 'formgenius', label: 'FormGenius' },
  { slug: 'mt-forge-internal', label: 'MT Forge (Internal)' },
];

const PLAN_TYPES = ['trial', 'monthly', 'annual', 'lifetime', 'enterprise'];

const PLAN_COLORS = {
  trial: { bg: '#fef3c7', text: '#92400e' },
  monthly: { bg: '#dbeafe', text: '#1e40af' },
  annual: { bg: '#d1fae5', text: '#065f46' },
  lifetime: { bg: '#ede9fe', text: '#5b21b6' },
  enterprise: { bg: '#fce7f3', text: '#9d174d' },
};

const PLAN_PRICES = {
  trial: 0,
  monthly: 49,
  annual: 499,
  lifetime: 1999,
  enterprise: 2499,
};

const APP_PREFIXES = {
  croppatrol: 'FH', 'forge-fleet': 'FL', 'directional-ops': 'DI', lotsync: 'MO',
  nursevault: 'NQ', 'nursevault-ca': 'NC', 'vantage-ops': 'SC', budledger: 'CB',
  ironledger: 'MN', haulsync: 'VH', glowdesk: 'BQ', closingdesk: 'RE',
  wrenchsync: 'MC', treadops: 'TS', herdledger: 'CT', vitasync: 'WL',
  policyforge: 'IB', 'mt-forge-internal': 'MT', stagevault: 'EV', formgenius: 'PW', all: 'UN',
};

// ============================================================
// HELPER: Generate random key string
// ============================================================
function randomBlock() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let block = '';
  for (let i = 0; i < 4; i++) {
    block += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return block;
}

function generateKeyStr(appSlug = 'all') {
  const prefix = APP_PREFIXES[appSlug] || 'UN';
  return `${prefix}-${randomBlock()}-${randomBlock()}-${randomBlock()}`;
}

function daysFromNow(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

function daysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function randomId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

// ============================================================
// DEMO DATA - 24 license keys across various apps and plans
// ============================================================
const DEMO_KEYS = [
  { id: randomId(), license_key: 'FH-K4M9-A2B7-X3P1', app_slug: 'croppatrol', plan_type: 'annual', max_users: 10, created_at: daysAgo(180), expires_at: daysFromNow(185), is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'Prairie Farms Co.', contact_email: 'john@prairiefarms.com', notes: 'Referred by Tyson', activations: [{ user_email: 'john@prairiefarms.com', activated_at: daysAgo(178) }, { user_email: 'mary@prairiefarms.com', activated_at: daysAgo(170) }, { user_email: 'steve@prairiefarms.com', activated_at: daysAgo(160) }] },
  { id: randomId(), license_key: 'FH-R7N3-C5D8-Z2Q4', app_slug: 'croppatrol', plan_type: 'monthly', max_users: 5, created_at: daysAgo(25), expires_at: daysFromNow(5), is_active: true, created_by: 'criodtech@gmail.com', company_name: 'Green Thumb Ranch', contact_email: 'lisa@greenthumb.com', notes: 'Expiring soon - follow up', activations: [{ user_email: 'lisa@greenthumb.com', activated_at: daysAgo(24) }] },
  { id: randomId(), license_key: 'FL-B8K2-D4F6-W9H3', app_slug: 'forge-fleet', plan_type: 'enterprise', max_users: 50, created_at: daysAgo(90), expires_at: daysFromNow(275), is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'TransCanada Logistics', contact_email: 'ops@transcanada-log.com', notes: 'Large fleet - 200+ trucks', activations: [{ user_email: 'ops@transcanada-log.com', activated_at: daysAgo(88) }, { user_email: 'dispatch@transcanada-log.com', activated_at: daysAgo(85) }, { user_email: 'mgr@transcanada-log.com', activated_at: daysAgo(80) }, { user_email: 'driver1@transcanada-log.com', activated_at: daysAgo(70) }] },
  { id: randomId(), license_key: 'DI-P3S7-E9G1-V6J5', app_slug: 'directional-ops', plan_type: 'annual', max_users: 25, created_at: daysAgo(120), expires_at: daysFromNow(245), is_active: true, created_by: 'criodtech@gmail.com', company_name: 'Apex Drilling Solutions', contact_email: 'admin@apexdrilling.com', notes: '', activations: [{ user_email: 'admin@apexdrilling.com', activated_at: daysAgo(118) }, { user_email: 'field@apexdrilling.com', activated_at: daysAgo(115) }] },
  { id: randomId(), license_key: 'MO-T5U9-F2H4-Y8L6', app_slug: 'lotsync', plan_type: 'monthly', max_users: 3, created_at: daysAgo(45), expires_at: daysFromNow(-15), is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'Valley Auto Group', contact_email: 'sales@valleyauto.com', notes: 'Expired - needs renewal follow-up', activations: [{ user_email: 'sales@valleyauto.com', activated_at: daysAgo(44) }] },
  { id: randomId(), license_key: 'NQ-A6B3-G7K1-M4N8', app_slug: 'nursevault', plan_type: 'enterprise', max_users: 100, created_at: daysAgo(200), expires_at: daysFromNow(165), is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'Mountain View Hospital', contact_email: 'it@mountainviewhosp.com', notes: 'Flagship healthcare client', activations: [{ user_email: 'it@mountainviewhosp.com', activated_at: daysAgo(198) }, { user_email: 'nurse1@mountainviewhosp.com', activated_at: daysAgo(190) }, { user_email: 'nurse2@mountainviewhosp.com', activated_at: daysAgo(185) }, { user_email: 'admin@mountainviewhosp.com', activated_at: daysAgo(180) }, { user_email: 'charge@mountainviewhosp.com', activated_at: daysAgo(175) }] },
  { id: randomId(), license_key: 'SC-C9D2-H5J7-P3R6', app_slug: 'vantage-ops', plan_type: 'annual', max_users: 15, created_at: daysAgo(60), expires_at: daysFromNow(305), is_active: true, created_by: 'criodtech@gmail.com', company_name: 'Elite HVAC Services', contact_email: 'owner@elitehvac.com', notes: 'HVAC vertical', activations: [{ user_email: 'owner@elitehvac.com', activated_at: daysAgo(58) }, { user_email: 'tech1@elitehvac.com', activated_at: daysAgo(55) }] },
  { id: randomId(), license_key: 'CB-E4F8-J6L3-Q9S2', app_slug: 'budledger', plan_type: 'annual', max_users: 10, created_at: daysAgo(150), expires_at: daysFromNow(215), is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'Rosebud Cannabis Farms', contact_email: 'che@rosebudcannabisfarms.ca', notes: 'Internal partner', activations: [{ user_email: 'che@rosebudcannabisfarms.ca', activated_at: daysAgo(148) }, { user_email: 'grow@rosebudcannabisfarms.ca', activated_at: daysAgo(140) }] },
  { id: randomId(), license_key: 'MN-G1H5-K8M2-T6V4', app_slug: 'ironledger', plan_type: 'enterprise', max_users: 50, created_at: daysAgo(30), expires_at: daysFromNow(335), is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'Northern Resources Ltd', contact_email: 'ops@northernresources.com', notes: 'New enterprise deal', activations: [{ user_email: 'ops@northernresources.com', activated_at: daysAgo(28) }] },
  { id: randomId(), license_key: 'VH-L3N7-P5R9-U2W6', app_slug: 'haulsync', plan_type: 'monthly', max_users: 5, created_at: daysAgo(20), expires_at: daysFromNow(10), is_active: true, created_by: 'criodtech@gmail.com', company_name: 'Clean Sweep Vac', contact_email: 'dispatch@cleansweep.com', notes: '', activations: [{ user_email: 'dispatch@cleansweep.com', activated_at: daysAgo(18) }] },
  { id: randomId(), license_key: 'BQ-S4T8-V6X3-A9C2', app_slug: 'glowdesk', plan_type: 'trial', max_users: 1, created_at: daysAgo(10), expires_at: daysFromNow(4), is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'Glow Studio', contact_email: 'owner@glowstudio.com', notes: 'Trial - follow up for conversion', activations: [{ user_email: 'owner@glowstudio.com', activated_at: daysAgo(9) }] },
  { id: randomId(), license_key: 'RE-D5E9-W7Y3-B4F6', app_slug: 'closingdesk', plan_type: 'annual', max_users: 10, created_at: daysAgo(100), expires_at: daysFromNow(265), is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'Summit Realty Group', contact_email: 'broker@summitrealty.com', notes: '', activations: [{ user_email: 'broker@summitrealty.com', activated_at: daysAgo(98) }, { user_email: 'agent1@summitrealty.com', activated_at: daysAgo(90) }, { user_email: 'agent2@summitrealty.com', activated_at: daysAgo(85) }] },
  { id: randomId(), license_key: 'MC-G6H3-X8Z2-C4E7', app_slug: 'wrenchsync', plan_type: 'monthly', max_users: 3, created_at: daysAgo(15), expires_at: daysFromNow(15), is_active: true, created_by: 'criodtech@gmail.com', company_name: 'Quick Fix Auto', contact_email: 'shop@quickfixauto.com', notes: '', activations: [{ user_email: 'shop@quickfixauto.com', activated_at: daysAgo(14) }] },
  { id: randomId(), license_key: 'TS-J7K2-A4B8-D6F9', app_slug: 'treadops', plan_type: 'annual', max_users: 5, created_at: daysAgo(75), expires_at: daysFromNow(290), is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'Tire City Plus', contact_email: 'manager@tirecity.com', notes: '', activations: [{ user_email: 'manager@tirecity.com', activated_at: daysAgo(73) }] },
  { id: randomId(), license_key: 'CT-L8M4-E2G5-H9J3', app_slug: 'herdledger', plan_type: 'annual', max_users: 10, created_at: daysAgo(210), expires_at: daysFromNow(155), is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'BigSky Cattle Co.', contact_email: 'ranch@bigskycattle.com', notes: 'Renewed once already', activations: [{ user_email: 'ranch@bigskycattle.com', activated_at: daysAgo(208) }, { user_email: 'foreman@bigskycattle.com', activated_at: daysAgo(200) }] },
  { id: randomId(), license_key: 'WL-N5P9-F3H7-K6M2', app_slug: 'vitasync', plan_type: 'trial', max_users: 1, created_at: daysAgo(7), expires_at: daysFromNow(7), is_active: true, created_by: 'criodtech@gmail.com', company_name: 'Zen Wellness Center', contact_email: 'info@zenwellness.com', notes: 'Trial started this week', activations: [{ user_email: 'info@zenwellness.com', activated_at: daysAgo(6) }] },
  { id: randomId(), license_key: 'IB-Q7R3-G5J8-L4N6', app_slug: 'policyforge', plan_type: 'monthly', max_users: 5, created_at: daysAgo(40), expires_at: daysFromNow(-10), is_active: false, created_by: 'rmiskulin@gmail.com', company_name: 'Shield Insurance', contact_email: 'agent@shieldins.com', notes: 'Revoked - non-payment', activations: [{ user_email: 'agent@shieldins.com', activated_at: daysAgo(38) }] },
  { id: randomId(), license_key: 'EV-S8T2-H4J6-M9P3', app_slug: 'stagevault', plan_type: 'annual', max_users: 10, created_at: daysAgo(50), expires_at: daysFromNow(315), is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'Grand Ballroom Events', contact_email: 'events@grandballroom.com', notes: '', activations: [{ user_email: 'events@grandballroom.com', activated_at: daysAgo(48) }, { user_email: 'coord@grandballroom.com', activated_at: daysAgo(40) }] },
  { id: randomId(), license_key: 'UN-U4V8-J2L5-N7Q9', app_slug: 'all', plan_type: 'lifetime', max_users: -1, created_at: daysAgo(365), expires_at: null, is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'Canstorr Inc.', contact_email: 'tyson@canstorr.com', notes: 'Strategic partner - unlimited access', activations: [{ user_email: 'tyson@canstorr.com', activated_at: daysAgo(360) }] },
  { id: randomId(), license_key: 'UN-W6X3-K4M8-R2S5', app_slug: 'all', plan_type: 'enterprise', max_users: -1, created_at: daysAgo(30), expires_at: daysFromNow(335), is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'Mt Forge (Internal)', contact_email: 'r.miskulin@mtforge.ai', notes: 'Internal team access', activations: [{ user_email: 'rmiskulin@gmail.com', activated_at: daysAgo(30) }, { user_email: 'criodtech@gmail.com', activated_at: daysAgo(29) }] },
  { id: randomId(), license_key: 'SC-A9B3-L5N7-T2V4', app_slug: 'vantage-ops', plan_type: 'monthly', max_users: 3, created_at: daysAgo(35), expires_at: daysFromNow(-5), is_active: true, created_by: 'criodtech@gmail.com', company_name: 'Plumb Perfect', contact_email: 'owner@plumbperfect.com', notes: 'Plumbing vertical - expired', activations: [{ user_email: 'owner@plumbperfect.com', activated_at: daysAgo(33) }] },
  { id: randomId(), license_key: 'NC-C5D9-M3P6-U8W2', app_slug: 'nursevault-ca', plan_type: 'annual', max_users: 25, created_at: daysAgo(45), expires_at: daysFromNow(320), is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'Alberta Health Services', contact_email: 'procurement@ahs.ca', notes: 'Canadian healthcare - large deal', activations: [{ user_email: 'procurement@ahs.ca', activated_at: daysAgo(43) }, { user_email: 'nurse.lead@ahs.ca', activated_at: daysAgo(40) }, { user_email: 'it.admin@ahs.ca', activated_at: daysAgo(38) }] },
  { id: randomId(), license_key: 'DI-E7F2-N4Q8-V6X9', app_slug: 'directional-ops', plan_type: 'trial', max_users: 1, created_at: daysAgo(12), expires_at: daysFromNow(2), is_active: true, created_by: 'criodtech@gmail.com', company_name: 'Westpoint Drilling', contact_email: 'tech@westpointdrill.com', notes: 'Trial ending soon', activations: [{ user_email: 'tech@westpointdrill.com', activated_at: daysAgo(11) }] },
  { id: randomId(), license_key: 'FL-G3H7-P5R2-W8Y4', app_slug: 'forge-fleet', plan_type: 'monthly', max_users: 5, created_at: daysAgo(28), expires_at: daysFromNow(2), is_active: true, created_by: 'rmiskulin@gmail.com', company_name: 'Express Courier Co.', contact_email: 'fleet@expresscourier.com', notes: '', activations: [{ user_email: 'fleet@expresscourier.com', activated_at: daysAgo(26) }, { user_email: 'driver@expresscourier.com', activated_at: daysAgo(20) }] },
];

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function LicenseManagerPage() {
  // State
  const [keys, setKeys] = useState(DEMO_KEYS);
  const [filterApp, setFilterApp] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showActivationsModal, setShowActivationsModal] = useState(null);
  const [showRenewModal, setShowRenewModal] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [notification, setNotification] = useState(null);

  // Generate form state
  const [genApp, setGenApp] = useState('all');
  const [genPlan, setGenPlan] = useState('monthly');
  const [genMaxUsers, setGenMaxUsers] = useState(5);
  const [genDays, setGenDays] = useState(30);
  const [genCompany, setGenCompany] = useState('');
  const [genEmail, setGenEmail] = useState('');
  const [genNotes, setGenNotes] = useState('');

  // Batch generate state
  const [batchCount, setBatchCount] = useState(5);
  const [batchApp, setBatchApp] = useState('all');
  const [batchPlan, setBatchPlan] = useState('monthly');
  const [batchMaxUsers, setBatchMaxUsers] = useState(1);
  const [batchDays, setBatchDays] = useState(30);

  // Renew state
  const [renewDays, setRenewDays] = useState(30);

  // ============================================================
  // COMPUTED VALUES
  // ============================================================
  const getKeyStatus = (key) => {
    if (!key.is_active) return 'revoked';
    if (key.expires_at && new Date(key.expires_at) <= new Date()) return 'expired';
    if (key.expires_at) {
      const daysLeft = Math.ceil((new Date(key.expires_at) - new Date()) / (1000 * 60 * 60 * 24));
      if (daysLeft <= 7) return 'expiring';
    }
    return 'active';
  };

  const filteredKeys = useMemo(() => {
    return keys.filter(k => {
      if (filterApp && k.app_slug !== filterApp) return false;
      if (filterPlan && k.plan_type !== filterPlan) return false;
      if (filterStatus) {
        const status = getKeyStatus(k);
        if (filterStatus === 'active' && status !== 'active' && status !== 'expiring') return false;
        if (filterStatus === 'expired' && status !== 'expired') return false;
        if (filterStatus === 'revoked' && status !== 'revoked') return false;
        if (filterStatus === 'expiring' && status !== 'expiring') return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          (k.company_name || '').toLowerCase().includes(q) ||
          (k.contact_email || '').toLowerCase().includes(q) ||
          k.license_key.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [keys, filterApp, filterPlan, filterStatus, searchQuery]);

  // Stats
  const stats = useMemo(() => {
    const now = new Date();
    const weekFromNow = new Date();
    weekFromNow.setDate(weekFromNow.getDate() + 7);

    const activeKeys = keys.filter(k => k.is_active && (!k.expires_at || new Date(k.expires_at) > now));
    const totalActivations = keys.reduce((sum, k) => sum + (k.activations?.length || 0), 0);
    const revenue = keys.reduce((sum, k) => {
      if (!k.is_active) return sum;
      return sum + (PLAN_PRICES[k.plan_type] || 0);
    }, 0);
    const expiringThisWeek = keys.filter(k => {
      if (!k.is_active || !k.expires_at) return false;
      const exp = new Date(k.expires_at);
      return exp > now && exp <= weekFromNow;
    });

    return { activeKeys: activeKeys.length, totalActivations, revenue, expiringThisWeek: expiringThisWeek.length };
  }, [keys]);

  // ============================================================
  // ACTIONS
  // ============================================================
  const showNotification = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key).then(() => {
      setCopiedKey(key);
      showNotification(`Copied: ${key}`);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  };

  const revokeKey = (keyId) => {
    setKeys(prev => prev.map(k =>
      k.id === keyId ? { ...k, is_active: false } : k
    ));
    showNotification('License key revoked.');
  };

  const reactivateKey = (keyId) => {
    setKeys(prev => prev.map(k =>
      k.id === keyId ? { ...k, is_active: true } : k
    ));
    showNotification('License key reactivated.');
  };

  const renewKey = (keyId, days) => {
    setKeys(prev => prev.map(k => {
      if (k.id !== keyId) return k;
      let baseDate = k.expires_at ? new Date(k.expires_at) : new Date();
      if (baseDate < new Date()) baseDate = new Date();
      baseDate.setDate(baseDate.getDate() + days);
      return { ...k, expires_at: baseDate.toISOString(), is_active: true };
    }));
    setShowRenewModal(null);
    showNotification(`Key renewed for ${days} days.`);
  };

  const generateSingleKey = () => {
    const newKey = {
      id: randomId(),
      license_key: generateKeyStr(genApp),
      app_slug: genApp,
      plan_type: genPlan,
      max_users: genMaxUsers,
      created_at: new Date().toISOString(),
      expires_at: genPlan === 'lifetime' ? null : daysFromNow(genDays),
      is_active: true,
      created_by: 'rmiskulin@gmail.com',
      company_name: genCompany,
      contact_email: genEmail,
      notes: genNotes,
      activations: [],
    };
    setKeys(prev => [newKey, ...prev]);
    setShowGenerateModal(false);
    setGenCompany('');
    setGenEmail('');
    setGenNotes('');
    showNotification(`Key generated: ${newKey.license_key}`);
  };

  const generateBatchKeys = () => {
    const newKeys = [];
    for (let i = 0; i < batchCount; i++) {
      newKeys.push({
        id: randomId(),
        license_key: generateKeyStr(batchApp),
        app_slug: batchApp,
        plan_type: batchPlan,
        max_users: batchMaxUsers,
        created_at: new Date().toISOString(),
        expires_at: batchPlan === 'lifetime' ? null : daysFromNow(batchDays),
        is_active: true,
        created_by: 'rmiskulin@gmail.com',
        company_name: '',
        contact_email: '',
        notes: 'Batch generated',
        activations: [],
      });
    }
    setKeys(prev => [...newKeys, ...prev]);
    setShowBatchModal(false);
    showNotification(`${batchCount} keys generated.`);
  };

  const formatDate = (iso) => {
    if (!iso) return 'Never';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysLeft = (expiresAt) => {
    if (!expiresAt) return null;
    const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getAppLabel = (slug) => {
    const app = APP_LIST.find(a => a.slug === slug);
    return app ? app.label.split(' (')[0] : slug;
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div>
      {/* Notification Toast */}
      {notification && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '12px 20px', borderRadius: 8,
          background: notification.type === 'success' ? '#065f46' : '#991b1b',
          color: '#fff', fontSize: 13, fontWeight: 600,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          animation: 'fadeIn 0.2s ease',
        }}>
          {notification.msg}
        </div>
      )}

      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#f1f5f9', margin: 0 }}>License Manager</h1>
          <p style={{ color: '#64748b', fontSize: 14, marginTop: 4 }}>Manage registration keys across all Forge applications</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowBatchModal(true)} style={{ ...s.btn, ...s.btnSecondary }}>
            Batch Generate
          </button>
          <button onClick={() => setShowGenerateModal(true)} style={{ ...s.btn, ...s.btnPrimary }}>
            + Generate Key
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={s.statCard}>
          <div style={s.statLabel}>Active Keys</div>
          <div style={s.statValue}>{stats.activeKeys}</div>
          <div style={{ ...s.statChange, color: '#10b981' }}>of {keys.length} total</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Total Activations</div>
          <div style={s.statValue}>{stats.totalActivations}</div>
          <div style={{ ...s.statChange, color: '#3b82f6' }}>users registered</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Est. Revenue</div>
          <div style={s.statValue}>${stats.revenue.toLocaleString()}</div>
          <div style={{ ...s.statChange, color: '#10b981' }}>active licenses</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Expiring This Week</div>
          <div style={{ ...s.statValue, color: stats.expiringThisWeek > 0 ? '#f59e0b' : '#f1f5f9' }}>{stats.expiringThisWeek}</div>
          <div style={{ ...s.statChange, color: stats.expiringThisWeek > 0 ? '#f59e0b' : '#64748b' }}>
            {stats.expiringThisWeek > 0 ? 'needs attention' : 'all good'}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by company, email, or key..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ ...s.filterInput, width: 280 }}
        />
        <select value={filterApp} onChange={(e) => setFilterApp(e.target.value)} style={s.filterSelect}>
          <option value="">All Apps</option>
          {APP_LIST.map(a => <option key={a.slug} value={a.slug}>{a.label}</option>)}
        </select>
        <select value={filterPlan} onChange={(e) => setFilterPlan(e.target.value)} style={s.filterSelect}>
          <option value="">All Plans</option>
          {PLAN_TYPES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={s.filterSelect}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="expiring">Expiring Soon</option>
          <option value="expired">Expired</option>
          <option value="revoked">Revoked</option>
        </select>
        {(filterApp || filterPlan || filterStatus || searchQuery) && (
          <button
            onClick={() => { setFilterApp(''); setFilterPlan(''); setFilterStatus(''); setSearchQuery(''); }}
            style={{ ...s.btn, ...s.btnSecondary, padding: '6px 12px', fontSize: 12 }}
          >
            Clear Filters
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>
          Showing {filteredKeys.length} of {keys.length} keys
        </span>
      </div>

      {/* Keys Table */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
            <thead>
              <tr>
                <th style={s.th}>License Key</th>
                <th style={s.th}>App</th>
                <th style={s.th}>Plan</th>
                <th style={s.th}>Company</th>
                <th style={s.th}>Users</th>
                <th style={s.th}>Created</th>
                <th style={s.th}>Expires</th>
                <th style={s.th}>Status</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredKeys.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 14 }}>
                    No license keys found matching your filters.
                  </td>
                </tr>
              ) : (
                filteredKeys.map(key => {
                  const status = getKeyStatus(key);
                  const daysLeft = getDaysLeft(key.expires_at);
                  const activeUsers = (key.activations || []).length;

                  return (
                    <tr key={key.id} style={{ borderBottom: '1px solid #1e293b' }}>
                      <td style={s.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <code style={{
                            fontFamily: '"JetBrains Mono", "SF Mono", monospace',
                            fontSize: 12,
                            color: copiedKey === key.license_key ? '#10b981' : '#e2e8f0',
                            letterSpacing: 0.5,
                          }}>
                            {key.license_key}
                          </code>
                          <button
                            onClick={() => copyKey(key.license_key)}
                            style={s.copyBtn}
                            title="Copy key"
                          >
                            {copiedKey === key.license_key ? 'ok' : 'cp'}
                          </button>
                        </div>
                      </td>
                      <td style={s.td}>
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>{getAppLabel(key.app_slug)}</span>
                      </td>
                      <td style={s.td}>
                        <span style={{
                          padding: '3px 8px',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          background: PLAN_COLORS[key.plan_type]?.bg || '#334155',
                          color: PLAN_COLORS[key.plan_type]?.text || '#94a3b8',
                        }}>
                          {key.plan_type.toUpperCase()}
                        </span>
                      </td>
                      <td style={s.td}>
                        <div>
                          <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 500 }}>{key.company_name || '--'}</div>
                          {key.contact_email && (
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{key.contact_email}</div>
                          )}
                        </div>
                      </td>
                      <td style={s.td}>
                        <span style={{ fontSize: 13, color: '#e2e8f0' }}>
                          {activeUsers}
                          <span style={{ color: '#64748b' }}>/{key.max_users <= 0 ? 'unlimited' : key.max_users}</span>
                        </span>
                      </td>
                      <td style={s.td}>
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>{formatDate(key.created_at)}</span>
                      </td>
                      <td style={s.td}>
                        <div>
                          <div style={{ fontSize: 12, color: daysLeft !== null && daysLeft <= 7 ? '#f59e0b' : '#94a3b8' }}>
                            {formatDate(key.expires_at)}
                          </div>
                          {daysLeft !== null && (
                            <div style={{
                              fontSize: 10,
                              color: daysLeft <= 0 ? '#ef4444' : daysLeft <= 7 ? '#f59e0b' : '#64748b',
                              marginTop: 2,
                              fontWeight: daysLeft <= 7 ? 600 : 400,
                            }}>
                              {daysLeft <= 0 ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d left`}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={s.td}>
                        <span style={{
                          padding: '3px 8px',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          background: status === 'active' ? '#10b981' : status === 'expiring' ? '#f59e0b' : status === 'expired' ? '#ef4444' : '#64748b',
                          color: '#fff',
                        }}>
                          {status.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ ...s.td, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => setShowActivationsModal(key)}
                            style={s.actionBtn}
                            title="View activations"
                          >
                            Users
                          </button>
                          <button
                            onClick={() => { setShowRenewModal(key); setRenewDays(30); }}
                            style={s.actionBtn}
                            title="Renew key"
                          >
                            Renew
                          </button>
                          {key.is_active ? (
                            <button
                              onClick={() => revokeKey(key.id)}
                              style={{ ...s.actionBtn, color: '#ef4444', borderColor: '#7f1d1d' }}
                              title="Revoke key"
                            >
                              Revoke
                            </button>
                          ) : (
                            <button
                              onClick={() => reactivateKey(key.id)}
                              style={{ ...s.actionBtn, color: '#10b981', borderColor: '#065f46' }}
                              title="Reactivate key"
                            >
                              Restore
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============================================================ */}
      {/* MODAL: Generate Single Key */}
      {/* ============================================================ */}
      {showGenerateModal && (
        <div style={s.modalOverlay} onClick={() => setShowGenerateModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>Generate License Key</h2>
              <button onClick={() => setShowGenerateModal(false)} style={s.modalClose}>X</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formField}>
                  <label style={s.formLabel}>Application</label>
                  <select value={genApp} onChange={(e) => setGenApp(e.target.value)} style={s.formInput}>
                    {APP_LIST.map(a => <option key={a.slug} value={a.slug}>{a.label}</option>)}
                  </select>
                </div>
                <div style={s.formField}>
                  <label style={s.formLabel}>Plan Type</label>
                  <select value={genPlan} onChange={(e) => setGenPlan(e.target.value)} style={s.formInput}>
                    {PLAN_TYPES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)} {PLAN_PRICES[p] > 0 ? `($${PLAN_PRICES[p]})` : '(Free)'}</option>)}
                  </select>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formField}>
                  <label style={s.formLabel}>Max Users</label>
                  <select value={genMaxUsers} onChange={(e) => setGenMaxUsers(parseInt(e.target.value))} style={s.formInput}>
                    <option value={1}>1 (Individual)</option>
                    <option value={3}>3 (Small Team)</option>
                    <option value={5}>5 (Team)</option>
                    <option value={10}>10 (Business)</option>
                    <option value={25}>25 (Large Business)</option>
                    <option value={50}>50 (Enterprise)</option>
                    <option value={100}>100 (Large Enterprise)</option>
                    <option value={-1}>Unlimited</option>
                  </select>
                </div>
                <div style={s.formField}>
                  <label style={s.formLabel}>Duration (Days)</label>
                  <select value={genDays} onChange={(e) => setGenDays(parseInt(e.target.value))} style={s.formInput} disabled={genPlan === 'lifetime'}>
                    <option value={14}>14 Days (Trial)</option>
                    <option value={30}>30 Days (Monthly)</option>
                    <option value={90}>90 Days (Quarterly)</option>
                    <option value={180}>180 Days (6 Months)</option>
                    <option value={365}>365 Days (Annual)</option>
                    <option value={730}>730 Days (2 Years)</option>
                  </select>
                  {genPlan === 'lifetime' && <span style={{ fontSize: 11, color: '#10b981', marginTop: 4 }}>Lifetime keys never expire</span>}
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formField}>
                  <label style={s.formLabel}>Company Name</label>
                  <input type="text" value={genCompany} onChange={(e) => setGenCompany(e.target.value)} placeholder="Customer company name" style={s.formInput} />
                </div>
                <div style={s.formField}>
                  <label style={s.formLabel}>Contact Email</label>
                  <input type="email" value={genEmail} onChange={(e) => setGenEmail(e.target.value)} placeholder="customer@company.com" style={s.formInput} />
                </div>
              </div>
              <div style={s.formField}>
                <label style={s.formLabel}>Notes</label>
                <textarea value={genNotes} onChange={(e) => setGenNotes(e.target.value)} placeholder="Optional notes about this key..." rows={3} style={{ ...s.formInput, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>

              {/* Preview */}
              <div style={{
                background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 16, marginTop: 8,
              }}>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Preview</div>
                <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.8 }}>
                  <span style={{ color: '#64748b' }}>App:</span> <strong style={{ color: '#e2e8f0' }}>{getAppLabel(genApp)}</strong><br />
                  <span style={{ color: '#64748b' }}>Plan:</span> <strong style={{ color: '#e2e8f0' }}>{genPlan}</strong> | <span style={{ color: '#64748b' }}>Users:</span> <strong style={{ color: '#e2e8f0' }}>{genMaxUsers <= 0 ? 'Unlimited' : genMaxUsers}</strong><br />
                  <span style={{ color: '#64748b' }}>Expires:</span> <strong style={{ color: '#e2e8f0' }}>{genPlan === 'lifetime' ? 'Never' : formatDate(daysFromNow(genDays))}</strong><br />
                  <span style={{ color: '#64748b' }}>Key Format:</span> <code style={{ color: '#4F46E5', fontFamily: 'monospace' }}>{APP_PREFIXES[genApp] || 'UN'}-XXXX-XXXX-XXXX</code>
                </div>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button onClick={() => setShowGenerateModal(false)} style={{ ...s.btn, ...s.btnSecondary }}>Cancel</button>
              <button onClick={generateSingleKey} style={{ ...s.btn, ...s.btnPrimary }}>Generate Key</button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL: Batch Generate */}
      {/* ============================================================ */}
      {showBatchModal && (
        <div style={s.modalOverlay} onClick={() => setShowBatchModal(false)}>
          <div style={{ ...s.modal, width: 500 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>Batch Generate Keys</h2>
              <button onClick={() => setShowBatchModal(false)} style={s.modalClose}>X</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formField}>
                  <label style={s.formLabel}>Number of Keys</label>
                  <select value={batchCount} onChange={(e) => setBatchCount(parseInt(e.target.value))} style={s.formInput}>
                    {[5, 10, 25, 50, 100].map(n => <option key={n} value={n}>{n} keys</option>)}
                  </select>
                </div>
                <div style={s.formField}>
                  <label style={s.formLabel}>Application</label>
                  <select value={batchApp} onChange={(e) => setBatchApp(e.target.value)} style={s.formInput}>
                    {APP_LIST.map(a => <option key={a.slug} value={a.slug}>{a.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formField}>
                  <label style={s.formLabel}>Plan Type</label>
                  <select value={batchPlan} onChange={(e) => setBatchPlan(e.target.value)} style={s.formInput}>
                    {PLAN_TYPES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <div style={s.formField}>
                  <label style={s.formLabel}>Max Users Each</label>
                  <select value={batchMaxUsers} onChange={(e) => setBatchMaxUsers(parseInt(e.target.value))} style={s.formInput}>
                    <option value={1}>1 (Individual)</option>
                    <option value={5}>5 (Team)</option>
                    <option value={10}>10 (Business)</option>
                  </select>
                </div>
              </div>
              <div style={s.formField}>
                <label style={s.formLabel}>Duration (Days)</label>
                <select value={batchDays} onChange={(e) => setBatchDays(parseInt(e.target.value))} style={s.formInput}>
                  <option value={14}>14 Days (Trial)</option>
                  <option value={30}>30 Days (Monthly)</option>
                  <option value={365}>365 Days (Annual)</option>
                </select>
              </div>
              <div style={{
                background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 12, marginTop: 8,
                fontSize: 12, color: '#94a3b8',
              }}>
                This will generate <strong style={{ color: '#f1f5f9' }}>{batchCount}</strong> individual keys for <strong style={{ color: '#f1f5f9' }}>{getAppLabel(batchApp)}</strong>.
                Each key will support up to <strong style={{ color: '#f1f5f9' }}>{batchMaxUsers}</strong> user(s).
                Keys can be assigned to customers after generation.
              </div>
            </div>
            <div style={s.modalFooter}>
              <button onClick={() => setShowBatchModal(false)} style={{ ...s.btn, ...s.btnSecondary }}>Cancel</button>
              <button onClick={generateBatchKeys} style={{ ...s.btn, ...s.btnPrimary }}>Generate {batchCount} Keys</button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL: View Activations */}
      {/* ============================================================ */}
      {showActivationsModal && (
        <div style={s.modalOverlay} onClick={() => setShowActivationsModal(null)}>
          <div style={{ ...s.modal, width: 560 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>Key Activations</h2>
                <code style={{ fontSize: 12, color: '#4F46E5', fontFamily: 'monospace' }}>{showActivationsModal.license_key}</code>
              </div>
              <button onClick={() => setShowActivationsModal(null)} style={s.modalClose}>X</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <div style={{ ...s.miniStat, flex: 1 }}>
                  <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Active Users</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9' }}>{(showActivationsModal.activations || []).length}</div>
                </div>
                <div style={{ ...s.miniStat, flex: 1 }}>
                  <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Max Users</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9' }}>{showActivationsModal.max_users <= 0 ? 'Unlimited' : showActivationsModal.max_users}</div>
                </div>
                <div style={{ ...s.miniStat, flex: 1 }}>
                  <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Company</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', marginTop: 2 }}>{showActivationsModal.company_name || '--'}</div>
                </div>
              </div>
              {(showActivationsModal.activations || []).length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#64748b', fontSize: 13 }}>
                  No activations yet. This key has not been used.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(showActivationsModal.activations || []).map((act, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', background: '#0f172a', border: '1px solid #334155', borderRadius: 8,
                    }}>
                      <div>
                        <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 500 }}>{act.user_email}</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Activated: {formatDate(act.activated_at)}</div>
                      </div>
                      <span style={{
                        padding: '2px 8px', background: '#10b981', color: '#fff',
                        borderRadius: 4, fontSize: 10, fontWeight: 600,
                      }}>ACTIVE</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              <button onClick={() => setShowActivationsModal(null)} style={{ ...s.btn, ...s.btnSecondary }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL: Renew Key */}
      {/* ============================================================ */}
      {showRenewModal && (
        <div style={s.modalOverlay} onClick={() => setShowRenewModal(null)}>
          <div style={{ ...s.modal, width: 440 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>Renew License Key</h2>
              <button onClick={() => setShowRenewModal(null)} style={s.modalClose}>X</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ marginBottom: 16 }}>
                <code style={{ fontSize: 14, color: '#4F46E5', fontFamily: 'monospace' }}>{showRenewModal.license_key}</code>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                  {showRenewModal.company_name || 'No company'} | Current expiry: {formatDate(showRenewModal.expires_at)}
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 12 }}>Add time to this license:</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                {[
                  { days: 30, label: '+30 Days' },
                  { days: 90, label: '+90 Days' },
                  { days: 180, label: '+6 Months' },
                  { days: 365, label: '+1 Year' },
                ].map(opt => (
                  <button
                    key={opt.days}
                    onClick={() => setRenewDays(opt.days)}
                    style={{
                      padding: '8px 16px',
                      background: renewDays === opt.days ? '#4F46E5' : '#0f172a',
                      border: `1px solid ${renewDays === opt.days ? '#4F46E5' : '#334155'}`,
                      borderRadius: 8,
                      color: renewDays === opt.days ? '#fff' : '#94a3b8',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div style={{
                background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 12,
                fontSize: 12, color: '#94a3b8',
              }}>
                New expiration will be: <strong style={{ color: '#10b981' }}>
                  {(() => {
                    let base = showRenewModal.expires_at ? new Date(showRenewModal.expires_at) : new Date();
                    if (base < new Date()) base = new Date();
                    base.setDate(base.getDate() + renewDays);
                    return formatDate(base.toISOString());
                  })()}
                </strong>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button onClick={() => setShowRenewModal(null)} style={{ ...s.btn, ...s.btnSecondary }}>Cancel</button>
              <button onClick={() => renewKey(showRenewModal.id, renewDays)} style={{ ...s.btn, ...s.btnPrimary }}>
                Renew (+{renewDays} days)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// INLINE STYLES
// ============================================================
const s = {
  // Buttons
  btn: {
    padding: '8px 16px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    transition: 'all 0.15s',
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #4F46E5, #7c3aed)',
    color: '#fff',
  },
  btnSecondary: {
    background: '#1e293b',
    color: '#e2e8f0',
    border: '1px solid #334155',
  },
  // Stat cards
  statCard: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 12,
    padding: 20,
  },
  statLabel: {
    fontSize: 12,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 600,
  },
  statValue: {
    fontSize: 28,
    fontWeight: 800,
    color: '#f1f5f9',
    marginTop: 4,
  },
  statChange: {
    fontSize: 12,
    marginTop: 4,
  },
  // Filter inputs
  filterInput: {
    padding: '8px 12px',
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 8,
    color: '#e2e8f0',
    fontSize: 13,
    outline: 'none',
  },
  filterSelect: {
    padding: '8px 12px',
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 8,
    color: '#e2e8f0',
    fontSize: 13,
    outline: 'none',
  },
  // Table
  th: {
    textAlign: 'left',
    padding: '12px 14px',
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    borderBottom: '1px solid #334155',
    background: '#0f172a',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px 14px',
    fontSize: 13,
    color: '#e2e8f0',
    borderBottom: '1px solid rgba(51,65,85,0.5)',
    verticalAlign: 'middle',
  },
  copyBtn: {
    background: '#0f172a',
    border: '1px solid #334155',
    color: '#64748b',
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: 10,
    cursor: 'pointer',
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  actionBtn: {
    padding: '4px 10px',
    background: 'none',
    border: '1px solid #334155',
    color: '#94a3b8',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
  },
  modal: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 16,
    width: 640,
    maxWidth: '90vw',
    maxHeight: '85vh',
    overflowY: 'auto',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid #334155',
  },
  modalClose: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    fontSize: 18,
    cursor: 'pointer',
    padding: '4px 8px',
    fontWeight: 700,
  },
  modalBody: {
    padding: 24,
  },
  modalFooter: {
    display: 'flex',
    gap: 12,
    justifyContent: 'flex-end',
    padding: '16px 24px',
    borderTop: '1px solid #334155',
  },
  // Forms
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 16,
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    marginBottom: 0,
  },
  formLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#94a3b8',
  },
  formInput: {
    padding: '10px 14px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 8,
    color: '#e2e8f0',
    fontSize: 14,
    outline: 'none',
  },
  // Mini stat in modal
  miniStat: {
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 8,
    padding: '10px 14px',
    textAlign: 'center',
  },
};
